import React, { useState, useEffect } from 'react';
import {
  collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, setDoc,
} from 'firebase/firestore';
import { db, CAMPAIGN_ID } from '../../firebase';
import { useMap } from '../../contexts/MapContext';
import { useCampaign } from '../../contexts/CampaignContext';
import { addToCollection } from '../../lib/api';
import { encodeTerrain, encodeFog, encodeElevation } from '../../lib/mapCodec';

const MAPS_COL = `campaigns/${CAMPAIGN_ID}/savedMaps`;

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 2)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24)  return `${hrs}h ago`;
  return `${days}d ago`;
}

export default function MapLibrary() {
  const { terrain, fog, elevation, gridW, gridH, walls, doors, loadFullMap, spawnPoint, setSpawnPoint } = useMap();
  const { tokens, removeToken, moveToken } = useCampaign();

  const [open,      setOpen]      = useState(false);
  const [mapName,   setMapName]   = useState('');
  const [saving,    setSaving]    = useState(false);
  const [loading,   setLoading]   = useState(null);
  const [deleting,  setDeleting]  = useState(null);
  const [savedMaps, setSavedMaps] = useState([]);
  const [fetching,  setFetching]  = useState(false);
  const [confirmLoad, setConfirmLoad] = useState(null);

  // Fetch saved maps whenever panel opens
  useEffect(() => {
    if (!open) return;
    fetchMaps();
  }, [open]);

  const fetchMaps = async () => {
    setFetching(true);
    try {
      const q    = query(collection(db, MAPS_COL), orderBy('savedAt', 'desc'));
      const snap = await getDocs(q);
      setSavedMaps(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('fetchMaps error', err);
    } finally {
      setFetching(false);
    }
  };

  const handleSave = async () => {
    const name = mapName.trim();
    if (!name) return;
    setSaving(true);
    try {
      // Collect non-player tokens (monsters, objects, NPCs)
      const savedTokens = Object.values(tokens)
        .filter((t) => t.type !== 'player')
        .map(({ id, ...rest }) => rest);

      const payload = {
        name,
        terrain:   encodeTerrain(terrain),
        fog:       encodeFog(fog),
        elevation: encodeElevation(elevation),
        gridW, gridH,
        walls:      walls  || [],
        doors:      doors  || [],
        tokens:     savedTokens,
        spawnPoint: spawnPoint || { x: 1, y: 1 },
        savedAt:    Date.now(),
        cellCount:  terrain.filter((t) => t !== 'empty').length,
      };

      // Check if a map with this name already exists — overwrite if so
      const existing = savedMaps.find((m) => m.name.toLowerCase() === name.toLowerCase());
      if (existing) {
        await setDoc(doc(db, MAPS_COL, existing.id), payload);
      } else {
        await addDoc(collection(db, MAPS_COL), payload);
      }
      setMapName('');
      await fetchMaps();
    } finally {
      setSaving(false);
    }
  };

  const handleLoad = async (map) => {
    setLoading(map.id);
    setConfirmLoad(null);
    try {
      // 1. Remove all non-player tokens and hide player tokens during transition
      const playerTokens = Object.values(tokens).filter((t) => t.type === 'player');
      const nonPlayerTokens = Object.values(tokens).filter((t) => t.type !== 'player');
      await Promise.all([
        ...nonPlayerTokens.map((t) => removeToken(t.id)),
        ...playerTokens.map((t) => moveToken(t.id, -10, -10)),  // move off-screen
      ]);

      // 2. Load terrain, walls, and doors
      await loadFullMap({
        name:    map.name,
        // Support both packed (new) and legacy cells[] format
        ...(map.terrain ? { terrain: map.terrain, fog: map.fog, elevation: map.elevation } : { cells: map.cells }),
        gridW:   map.gridW  || gridW,
        gridH:   map.gridH  || gridH,
        walls:   map.walls  || [],
        doors:   map.doors  || [],
      });

      // 3. Restore spawn point from saved map
      const sp = map.spawnPoint || { x: 1, y: 1 };
      setSpawnPoint(sp.x, sp.y);

      // 4. Re-spawn saved tokens (monsters, objects, NPCs) with exact positions
      if (map.tokens && map.tokens.length > 0) {
        for (const tokenData of map.tokens) {
          await addToCollection(`campaigns/${CAMPAIGN_ID}/tokens`, tokenData);
        }
      }

      // 5. Move player tokens to the restored spawn point
      for (const t of playerTokens) {
        await moveToken(t.id, sp.x, sp.y);
      }
    } finally {
      setLoading(null);
    }
  };

  const handleDelete = async (map) => {
    setDeleting(map.id);
    try {
      await deleteDoc(doc(db, MAPS_COL, map.id));
      setSavedMaps((prev) => prev.filter((m) => m.id !== map.id));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={styles.wrapper}>
      <button style={styles.toggle} onClick={() => setOpen((v) => !v)}>
        <span>🗺 Map Library</span>
        <span style={styles.chevron}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={styles.body}>
          {/* Save current map */}
          <div style={styles.saveRow}>
            <input
              style={styles.nameInput}
              placeholder="Map name…"
              value={mapName}
              onChange={(e) => setMapName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            />
            <button
              style={styles.saveBtn}
              onClick={handleSave}
              disabled={!mapName.trim() || saving}
            >
              {saving ? '…' : '💾 Save'}
            </button>
          </div>

          {/* Saved maps list */}
          {fetching && <div style={styles.hint}>Loading…</div>}
          {!fetching && savedMaps.length === 0 && (
            <div style={styles.hint}>No saved maps yet.</div>
          )}

          {savedMaps.map((map) => {
            const isConfirming = confirmLoad === map.id;
            return (
              <div key={map.id} style={styles.mapEntry}>
                <div style={styles.mapInfo}>
                  <span style={styles.mapName}>{map.name}</span>
                  <span style={styles.mapMeta}>
                    {map.cellCount ?? '?'} cells{map.tokens?.length ? ` · ${map.tokens.length} tokens` : ''}{map.walls?.length ? ` · ${map.walls.length} walls` : ''} · {timeAgo(map.savedAt)}
                  </span>
                </div>

                {isConfirming ? (
                  <div style={styles.confirmRow}>
                    <span style={styles.confirmText}>Replace map, tokens &amp; walls?</span>
                    <button style={styles.confirmYes} onClick={() => handleLoad(map)}>
                      {loading === map.id ? '…' : 'Yes'}
                    </button>
                    <button style={styles.confirmNo} onClick={() => setConfirmLoad(null)}>No</button>
                  </div>
                ) : (
                  <div style={styles.mapActions}>
                    <button
                      style={styles.loadBtn}
                      onClick={() => setConfirmLoad(map.id)}
                      disabled={!!loading}
                    >
                      Load
                    </button>
                    <button
                      style={styles.deleteBtn}
                      onClick={() => handleDelete(map)}
                      disabled={deleting === map.id}
                      title="Delete map"
                    >
                      {deleting === map.id ? '…' : '🗑'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrapper:      { borderTop: '1px solid #4a3d28', flexShrink: 0 },
  toggle:       { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer' },
  chevron:      { fontSize: 10, color: '#6a5d45' },
  body:         { padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  saveRow:      { display: 'flex', gap: 6 },
  nameInput:    { flex: 1, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '7px 10px', fontSize: 13, outline: 'none' },
  saveBtn:      { background: '#3a2060', border: '1px solid #d4a847', borderRadius: 4, color: '#d4a847', fontSize: 12, fontWeight: 700, padding: '0 12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  hint:         { fontSize: 11, color: '#6a5d45', textAlign: 'center', padding: '4px 0' },
  mapEntry:     { background: '#151525', border: '1px solid #4a3d28', borderRadius: 4, padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 },
  mapInfo:      { display: 'flex', flexDirection: 'column', gap: 2 },
  mapName:      { fontSize: 13, fontWeight: 700, color: '#f0e6d0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  mapMeta:      { fontSize: 11, color: '#6a5d45' },
  mapActions:   { display: 'flex', gap: 6, alignItems: 'center' },
  loadBtn:      { flex: 1, height: 28, background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 6, color: '#d4a847', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  deleteBtn:    { width: 28, height: 28, background: 'transparent', border: 'none', color: '#6a5d45', fontSize: 13, cursor: 'pointer', borderRadius: 5 },
  confirmRow:   { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  confirmText:  { fontSize: 11, color: '#d4943a', flex: 1 },
  confirmYes:   { padding: '3px 10px', background: '#d4a847', border: 'none', borderRadius: 5, color: '#0a0f1e', fontSize: 11, fontWeight: 800, cursor: 'pointer' },
  confirmNo:    { padding: '3px 10px', background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 5, color: '#9a8b6e', fontSize: 11, cursor: 'pointer' },
};
